import AppButton from "@/components/AppButton";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import { useRef, useEffect, useState } from "react";

const AppDownloadSection = () => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // 尝试播放视频的函数
  const attemptPlay = () => {
    const videoElement = videoRef.current;
    if (videoElement) {
      // 预加载视频内容
      videoElement.load();
      
      // 重复尝试播放，因为在某些情况下第一次可能会失败
      const playPromise = videoElement.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log("Video playback started successfully");
          })
          .catch(e => {
            console.log("Video autoplay prevented, retrying in 1 second:", e);
            // 如果第一次尝试失败，1秒后再次尝试
            setTimeout(() => {
              videoElement.play().catch(err => 
                console.log("Second play attempt failed:", err)
              );
            }, 1000);
          });
      }
    }
  };
  
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    
    // 防止右键菜单显示
    const preventContextMenu = (e: Event) => {
      e.preventDefault();
      return false;
    };
    
    // 确保视频循环播放
    const handleVideoEnd = () => {
      videoElement.currentTime = 0;
      videoElement.play().catch(e => console.log("Video replay prevented:", e));
    };
    
    // 设置视频事件监听器
    videoElement.addEventListener('ended', handleVideoEnd);
    videoElement.addEventListener('contextmenu', preventContextMenu);
    
    // 使用IntersectionObserver检测视频何时可见
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          // 当视频进入视口时
          if (entry.isIntersecting) {
            setIsVisible(true);
            attemptPlay();
          }
        });
      },
      { threshold: 0.1 } // 当10%的视频可见时触发
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    // 在组件卸载时清理
    return () => {
      videoElement.removeEventListener('ended', handleVideoEnd);
      videoElement.removeEventListener('contextmenu', preventContextMenu);
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
      observer.disconnect();
    };
  }, []);
  
  // 当视频变为可见时尝试播放
  useEffect(() => {
    if (isVisible) {
      attemptPlay();
    }
  }, [isVisible]);
  
  return (
    <motion.section 
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: "-100px" }}
      className="py-16 bg-black"
    >
      <div className="container mx-auto px-3 sm:px-6">
        <motion.div 
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row items-center gap-12 p-4 sm:p-10 rounded-xl w-full"
        >
          <motion.div 
            initial={{ x: -20, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex-1 w-full"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-white font-sans">{t('app.download.title', 'Download the Anzir App')}</h2>
            <p className="text-gray-300 mb-8">
              {t('app.download.description', 'Take your MysteryAct experience to the next level with our mobile app. Make reservations, connect with players, and receive notifications for offline events near you.')}
            </p>
            <div className="flex flex-row gap-4 justify-center sm:justify-start">
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className="w-auto"
              >
                <a href={import.meta.env.VITE_APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <img 
                    src="/img/appStore.png" 
                    alt="Download on the App Store" 
                    className="h-12 w-auto"
                  />
                </a>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className="w-auto"
              >
                <a href={import.meta.env.VITE_GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
                  <img 
                    src="/img/googlePlay.png" 
                    alt="Get it on Google Play" 
                    className="h-12 w-auto"
                  />
                </a>
              </motion.div>
            </div>
          </motion.div>
          <motion.div 
            initial={{ x: 20, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="flex-1 flex justify-center"
          >
            <div ref={containerRef} className="max-w-[450px] w-full">
              <video 
                ref={videoRef}
                src="https://static.anzirtech.com/website/promotional.mov"
                className="w-full h-full object-cover"
                muted
                loop
                autoPlay
                playsInline
                preload="auto"
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default AppDownloadSection;
